/**
 * Presentation Library Search Agent - Evaluation Section
 * Section 5: Final Reminders, Explanations, Evaluation Criteria
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { SECURITY_WARNING_SHORT } from '../../../base/systemPrompt/shared';
import { getSharedVariable } from '../../../../prompts';
import { IN_PRESENTATION_SEARCH_PROMPT_KEYS } from './variables';

class PresentationLibrarySearchEvaluationSection extends PromptSectionBase {
    protected readonly keywordsKey = IN_PRESENTATION_SEARCH_PROMPT_KEYS.evaluation;
    protected readonly fallbackContent = `## Example Workflow

1. **Search Call** (semantic search):
\`\`\`json
{
  "documentId": "CURRENT",
  "searchType": "semantic",
  "resultTypes": ["slide", "component"],
  "query": "quarterly revenue growth chart",
  "numResults": 10
}
\`\`\`

2. **Submit Results Call**:
\`\`\`json
{
  "results": [
    {
      "requirementId": "A",
      "relevantResults": {
        "searchResults": ["slide_257", "component_abc12345"]
      },
      "rationale": "Found slide 257 with Q4 revenue chart and a component showing growth trends."
    }
  ],
  "summary": "Found relevant prior examples for revenue visualizations"
}
\`\`\`

Scope guidance:
- Use \`documentIds\` when the request targets a known set of presentations.
- Use \`documentId: "ALL"\` for global historical ideation across backend indexed decks.

---

## Evaluation Criteria

Your performance is evaluated on:

### Search Effectiveness
- Did you choose the right search type?
- Did you select appropriate result types?
- Did you find the most relevant content?

### Result Quality
- Are the result IDs correct and relevant?
- Do the rationales clearly explain what was found?
- Did you address ALL requirements?

### Efficiency
- Did you use excludeAllPrevious to avoid duplicates?
- Did you batch related searches appropriately?

---

## Final Reminders

1. **ALWAYS use submitResults** - Never just respond with text
2. **Choose appropriate search type** - semantic for concepts, exact for specific text
3. **Include result IDs** - Use the exact IDs from search results
4. **Write good rationales** - Explain what you found and why it's relevant

{{securityWarnings}}`;
}

const evaluationSection = new PresentationLibrarySearchEvaluationSection();

/**
 * Get the evaluation section.
 * Uses Keywords AI if available, otherwise falls back to code.
 * Supports {{securityWarnings}} variable replacement.
 */
export function getEvaluationSection(): string {
    return evaluationSection.getContentWithVariables({
        securityWarnings: getSharedVariable('securityWarnings'),
    });
}

/**
 * Export constant for backwards compatibility
 */
export const IN_PRESENTATION_EVALUATION = evaluationSection.getFallbackContent();
